import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Icon, type IconSlot } from "../Icons/Icons";
import { type Placement, Tooltip } from "../Tooltip/Tooltip";
import "./DropdownMenu.css";

const CLOSE_TIMEOUT_DELAY = 200;
const SUBMENU_CLOSE_DELAY = 150;
/** Breathing room kept between a flipped submenu and the viewport edge. */
const VIEWPORT_MARGIN = 8;

/**
 * Configuration object for a single dropdown menu item
 */
export interface DropdownMenuItem {
  /** Item type discriminator (optional for backward compatibility) */
  type?: "item";
  /** Display text for the menu item */
  label: string;
  /** Callback function executed when the item is clicked */
  onClick?: () => void;
  /** Icon name from design system icon registry or custom icon component */
  icon?: IconSlot;
  /** Optional CSS color value for the text, and the icon beside it */
  textColor?: string;
  /** Whether the item is disabled */
  disabled?: boolean;
  /**
   * Optional hint shown on hover/focus. Works on disabled items too — the
   * trigger is a wrapper element, not the (event-suppressing) disabled button.
   */
  tooltip?: ReactNode;
  /**
   * Keeps the tooltip open while the pointer is inside it, so interactive
   * content (e.g. a link) stays clickable.
   */
  tooltipInteractive?: boolean;
  /**
   * Marks the item as the chosen one in a set, drawing a trailing check.
   * Use for menu rows that report state (a toggled option, the active choice
   * in a submenu) rather than firing a one-shot action. Items carrying this
   * are exposed as `menuitemradio`/`menuitemcheckbox` rather than `menuitem`.
   */
  selected?: boolean;
  /**
   * Which ARIA role a `selected` item takes. `radio` (default) is right for
   * one-of-many groups such as a submenu of levels; `checkbox` for an
   * independent on/off row.
   */
  selectionMode?: "radio" | "checkbox";
  /** Nested menu entries */
  items?: DropdownMenuEntry[];
  /** Alternative nested menu entries alias */
  children?: DropdownMenuEntry[];
}

/**
 * Configuration object for a dropdown menu divider
 */
export interface DropdownMenuDivider {
  /** Divider type discriminator */
  type: "divider";
}

/**
 * Union type for all dropdown menu entries (items or dividers)
 */
export type DropdownMenuEntry = DropdownMenuItem | DropdownMenuDivider;

/**
 * Icon size variants
 */
export type ContentSize = "sm" | "md" | "lg";

/**
 * Internal context shared between DropdownMenu, DropdownMenuList, and DropdownMenuNode.
 * Avoids passing these as props through every level and, critically, allows the
 * child components to be defined at module scope so React preserves their identity
 * across parent re-renders (preventing unmount / remount of the entire menu tree).
 */
interface DropdownMenuContextValue {
  onHoverOpen: boolean;
  submenuOnHover: boolean;
  contentSize: ContentSize;
  closeMenu: () => void;
  renderIcon: (icon: IconSlot) => ReactNode;
  getNestedEntries: (item: DropdownMenuItem) => DropdownMenuEntry[];
}

const DropdownMenuContext = createContext<DropdownMenuContextValue>({
  onHoverOpen: true,
  submenuOnHover: true,
  contentSize: "md",
  closeMenu: () => {
    /* no-op */
  },
  renderIcon: () => null,
  getNestedEntries: () => [],
});

// ---------------------------------------------------------------------------
// DropdownMenuList — defined at module level to keep a stable component identity
// ---------------------------------------------------------------------------

interface DropdownMenuListProps {
  entries: DropdownMenuEntry[];
  parentItemRef?: React.RefObject<HTMLButtonElement | null>;
  isSubmenu?: boolean;
}

const DropdownMenuList: FC<DropdownMenuListProps> = ({
  entries,
  parentItemRef,
  isSubmenu = false,
}) => {
  const { getNestedEntries } = useContext(DropdownMenuContext);

  return (
    <ul className="indigo-dropdown-actions-list" role="menu">
      {entries.map((entry, index) => {
        if (entry.type === "divider") {
          return (
            <li
              key={index}
              className="indigo-dropdown-actions-divider"
              role="separator"
            />
          );
        }

        const item = entry;
        const nestedEntries = getNestedEntries(item);
        const hasNestedEntries = nestedEntries.length > 0;

        return (
          <DropdownMenuNode
            key={`${item.label}-${index}`}
            item={item}
            hasNestedEntries={hasNestedEntries}
            nestedEntries={nestedEntries}
            parentItemRef={parentItemRef}
            isSubmenu={isSubmenu}
          />
        );
      })}
    </ul>
  );
};

// ---------------------------------------------------------------------------
// DropdownMenuNode — defined at module level to keep a stable component identity
// ---------------------------------------------------------------------------

interface DropdownMenuNodeProps {
  item: DropdownMenuItem;
  hasNestedEntries: boolean;
  nestedEntries: DropdownMenuEntry[];
  parentItemRef?: React.RefObject<HTMLButtonElement | null>;
  isSubmenu: boolean;
}

const DropdownMenuNode: FC<DropdownMenuNodeProps> = ({
  item,
  hasNestedEntries,
  nestedEntries,
  parentItemRef,
  isSubmenu,
}) => {
  const { submenuOnHover, contentSize, closeMenu, renderIcon } =
    useContext(DropdownMenuContext);

  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [openToLeft, setOpenToLeft] = useState(false);
  const [verticalShift, setVerticalShift] = useState(0);
  const itemRef = useRef<HTMLButtonElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const submenuCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up submenu close timeout on unmount
  useEffect(() => {
    return () => {
      if (submenuCloseTimeoutRef.current) {
        clearTimeout(submenuCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isSubmenuOpen || !itemRef.current) {
      return;
    }

    const itemRect = itemRef.current.getBoundingClientRect();
    const submenuWidth = submenuRef.current?.offsetWidth || 180;

    let clippingParent: HTMLElement | null = null;
    let parent = itemRef.current.parentElement;
    while (parent && parent !== document.body) {
      const { overflow, overflowX } = getComputedStyle(parent);
      if (
        ["auto", "scroll", "hidden"].includes(overflow) ||
        ["auto", "scroll", "hidden"].includes(overflowX)
      ) {
        clippingParent = parent;
        break;
      }
      parent = parent.parentElement;
    }

    const rightBoundary = clippingParent
      ? clippingParent.getBoundingClientRect().right
      : window.innerWidth;

    const wouldOverflowRight = itemRect.right + submenuWidth > rightBoundary;
    const hasSpaceOnLeft = itemRect.left - submenuWidth >= 0;

    setOpenToLeft(wouldOverflowRight && hasSpaceOnLeft);

    // The submenu is anchored to the item's top and grows downward, so an item
    // near the bottom of the screen ran its submenu straight off the edge —
    // which is exactly where this menu lives when it opens upward from a
    // composer. Slide it up by the overflow, without letting it escape the top.
    // The menu container is `position: fixed`, so the viewport is the real
    // clip regardless of any scrolling ancestor.
    const submenuHeight = submenuRef.current?.offsetHeight ?? 0;
    const overflowBottom =
      itemRect.top + submenuHeight + VIEWPORT_MARGIN - window.innerHeight;
    let shift = overflowBottom > 0 ? -overflowBottom : 0;
    if (itemRect.top + shift < VIEWPORT_MARGIN) {
      shift = VIEWPORT_MARGIN - itemRect.top;
    }
    setVerticalShift(shift);
  }, [isSubmenuOpen]);

  const closeCurrentSubmenu = () => {
    setIsSubmenuOpen(false);
  };

  const openCurrentSubmenu = () => {
    // Cancel any pending close when re-entering the item
    if (submenuCloseTimeoutRef.current) {
      clearTimeout(submenuCloseTimeoutRef.current);
      submenuCloseTimeoutRef.current = null;
    }
    if (!item.disabled) {
      setIsSubmenuOpen(true);
    }
  };

  const scheduleSubmenuClose = () => {
    // Delay submenu close so the mouse can travel to the submenu
    // without the submenu disappearing due to brief mouseleave events
    submenuCloseTimeoutRef.current = setTimeout(() => {
      setIsSubmenuOpen(false);
      submenuCloseTimeoutRef.current = null;
    }, SUBMENU_CLOSE_DELAY);
  };

  const handleItemClick = () => {
    if (item.disabled) {
      return;
    }

    if (hasNestedEntries) {
      if (submenuCloseTimeoutRef.current) {
        clearTimeout(submenuCloseTimeoutRef.current);
        submenuCloseTimeoutRef.current = null;
      }
      // In hover mode the pointer is already over the row, so toggling would
      // close a submenu that hover immediately wants open again — and no fresh
      // mouseenter arrives to reopen it. Clicking there just affirms "open".
      setIsSubmenuOpen((prev) => (submenuOnHover ? true : !prev));
      return;
    }

    item.onClick?.();
    closeMenu();
  };

  const handleItemKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (item.disabled) {
      return;
    }

    if (hasNestedEntries && event.key === "ArrowRight") {
      event.preventDefault();
      setIsSubmenuOpen(true);

      requestAnimationFrame(() => {
        const firstItem =
          submenuRef.current?.querySelector<HTMLButtonElement>(
            '[role="menuitem"]',
          );
        firstItem?.focus();
      });
      return;
    }

    if (isSubmenu && event.key === "ArrowLeft") {
      event.preventDefault();
      closeCurrentSubmenu();
      parentItemRef?.current?.focus();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();

      if (isSubmenu) {
        closeCurrentSubmenu();
        parentItemRef?.current?.focus();
      } else {
        closeMenu();
      }
      return;
    }

    if ((event.key === "Enter" || event.key === " ") && hasNestedEntries) {
      event.preventDefault();
      setIsSubmenuOpen((prev) => !prev);
    }
  };

  const itemButton = (
    <button
      ref={itemRef}
      // Same reason as the trigger: never a submit. The menu is portaled to
      // <body> so a form rarely encloses it, but that is positioning, not a
      // guarantee — the no-document branch renders it in place.
      type="button"
      onClick={handleItemClick}
      onKeyDown={handleItemKeyDown}
      disabled={item.disabled}
      className={`indigo-dropdown-actions-item ${hasNestedEntries ? "indigo-dropdown-actions-item-trigger" : ""}${item.selected ? " is-selected" : ""}`}
      style={{ color: item.textColor }}
      role={
        item.selected === undefined
          ? "menuitem"
          : item.selectionMode === "checkbox"
            ? "menuitemcheckbox"
            : "menuitemradio"
      }
      aria-checked={item.selected}
      tabIndex={0}
      aria-haspopup={hasNestedEntries ? "menu" : undefined}
      aria-expanded={hasNestedEntries ? isSubmenuOpen : undefined}
    >
      {item.icon && (
        <span
          className={`indigo-dropdown-actions-item-icon indigo-dropdown-actions-item-icon-${contentSize}`}
          // A coloured item is coloured whole: without this the icon keeps the
          // stylesheet's tertiary grey beside a red "Delete".
          style={item.textColor ? { color: item.textColor } : undefined}
        >
          {renderIcon(item.icon)}
        </span>
      )}
      <span
        className={`indigo-dropdown-actions-item-text indigo-dropdown-actions-item-text-${contentSize}`}
      >
        {item.label}
      </span>
      {item.selected && (
        <span className="indigo-dropdown-actions-item-check" aria-hidden="true">
          <Icon name="check" size="xs" />
        </span>
      )}
      {hasNestedEntries && (
        <span
          className="indigo-dropdown-actions-submenu-arrow"
          aria-hidden="true"
        >
          {/* `right` was never in the icon registry: Icon logged a
              "not found" error and rendered nothing, so this arrow has never
              actually drawn. `chevron-right` is the registered glyph, but its
              path covers only the central ~4/28 of a 28-unit viewBox, so a
              12px icon renders a ~2px speck. It is drawn oversized and the
              empty margins are cropped by the wrapper — see the CSS. 24px puts
              the stroke on a par with the row label; heavier reads chunky. */}
          <Icon name="chevron-right" sizeX={24} sizeY={24} />
        </span>
      )}
    </button>
  );

  return (
    <li
      className="indigo-dropdown-actions-item-wrapper"
      onMouseEnter={submenuOnHover ? openCurrentSubmenu : undefined}
      onMouseLeave={submenuOnHover ? scheduleSubmenuClose : undefined}
    >
      {/* A disabled <button> suppresses its own hover/focus events, so the
          tooltip is anchored to a wrapper <span> that still receives them. */}
      {item.tooltip ? (
        <Tooltip
          content={item.tooltip}
          placement="left"
          interactive={item.tooltipInteractive}
        >
          <span className="indigo-dropdown-actions-item-tooltip-target">
            {itemButton}
          </span>
        </Tooltip>
      ) : (
        itemButton
      )}

      {hasNestedEntries && (
        <div
          ref={submenuRef}
          className={`indigo-dropdown-actions-submenu ${isSubmenuOpen ? "open" : ""} ${openToLeft ? "open-left" : "open-right"}`}
          style={{ top: verticalShift }}
        >
          <DropdownMenuList
            entries={nestedEntries}
            parentItemRef={itemRef}
            isSubmenu
          />
        </div>
      )}
    </li>
  );
};

// ---------------------------------------------------------------------------
// DropdownMenu — main component
// ---------------------------------------------------------------------------

/**
 * Props for the DropdownMenu component
 */
export interface DropdownMenuProps {
  /** Array of menu items or dividers to display in the dropdown */
  items: DropdownMenuEntry[];
  /** Additional CSS classes for the trigger button */
  buttonClassName?: string;
  /** Icon for the trigger button (design system icon name or custom component) */
  buttonIcon?: IconSlot;
  /** Alternative icon shown on hover */
  hoverButtonIcon?: IconSlot;
  /** Direction where the dropdown opens relative to the button */
  dropDirection?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  /** Whether to open dropdown on hover (true) or click only (false) */
  onHoverOpen?: boolean;
  /**
   * Whether a nested submenu opens on hover. Defaults to `onHoverOpen`, so
   * existing callers keep the behaviour they had. Set it independently for the
   * conventional pattern — the menu itself opens on click, but once it is open
   * its submenus expand on hover, the way Ant Design, macOS and Windows menus
   * all behave.
   */
  submenuOnHover?: boolean;
  /** Test identifier for automated testing */
  "data-testid"?: string;
  /** Whether the dropdown trigger button is disabled */
  disabled?: boolean;
  /** Custom width for the dropdown menu */
  menuWidth?: string;
  /** Size of icons and text in menu items (sm: 12px, md: 16px, lg: 20px) */
  contentSize?: ContentSize;
  /**
   * Replaces the built-in icon button with your own trigger content. The
   * component still owns the surrounding <button> — its open/close, keyboard
   * and ARIA wiring — so pass the visual only (an icon, a label, a spinner),
   * not another button. `buttonIcon` is ignored when this is set.
   */
  trigger?: ReactNode;
  /** Accessible name for the trigger when it renders icon-only content */
  triggerAriaLabel?: string;
  /**
   * Hint shown on hovering the trigger. Anchored to the trigger button itself,
   * so it does not follow the pointer into the open menu — wrapping the whole
   * component from outside would, since the menu is a DOM descendant of the
   * same root. Suppressed while the menu is open, where it would only overlap
   * the thing it just described.
   */
  triggerTooltip?: ReactNode;
  /** Placement of `triggerTooltip` relative to the trigger */
  triggerTooltipPlacement?: Placement;
  /**
   * Surface the menu is anchored against.
   *
   * `dark` for a menu opened from a dark chrome — the PDF viewer's toolbar —
   * where the default light panel reads as a different application's window
   * rather than as part of the bar it came out of.
   */
  tone?: "light" | "dark";
}

/**
 * DropdownMenu - A flexible dropdown menu component with hover and click interactions
 *
 * Features:
 * - Configurable trigger button with icon support
 * - Hover and click-based opening modes
 * - Left/right positioning options
 * - Custom styling support
 * - Keyboard accessibility
 * - Click-outside closing behavior
 *
 * @example
 * ```tsx
 * const menuItems = [
 *   { label: "Edit", onClick: () => console.log("Edit"), icon: "edit" },
 *   { label: "Delete", onClick: () => console.log("Delete"), icon: "trash", textColor: "red" }
 * ];
 *
 * <DropdownMenu
 *   items={menuItems}
 *   buttonIcon="more-vertical"
 *   dropDirection="bottom-right"
 *   onHoverOpen={true}
 * />
 * ```
 */
export const DropdownMenu: FC<DropdownMenuProps> = ({
  items,
  buttonClassName,
  buttonIcon,
  hoverButtonIcon,
  dropDirection = "bottom-left",
  onHoverOpen = true,
  submenuOnHover,
  disabled = false,
  menuWidth,
  contentSize = "md",
  trigger,
  triggerAriaLabel,
  triggerTooltip,
  triggerTooltipPlacement = "top",
  tone = "light",
  "data-testid": dataTestId,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const computeMenuPosition = useCallback(() => {
    if (!buttonRef.current || !menuRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const el = menuRef.current;
    if (dropDirection.startsWith("bottom")) {
      el.style.top = `${rect.bottom + 4}px`;
      el.style.bottom = "auto";
    } else {
      el.style.bottom = `${window.innerHeight - rect.top + 13}px`;
      el.style.top = "auto";
    }
    if (dropDirection.endsWith("left")) {
      el.style.right = `${window.innerWidth - rect.right}px`;
      el.style.left = "auto";
    } else {
      el.style.left = `${rect.left}px`;
      el.style.right = "auto";
    }
  }, [dropDirection]);

  const getNestedEntries = useCallback(
    (item: DropdownMenuItem): DropdownMenuEntry[] =>
      item.items || item.children || [],
    [],
  );

  /**
   * Renders an icon - either from design system or custom component
   */
  const renderIcon = useCallback((icon: IconSlot) => {
    if (typeof icon === "string") {
      return <Icon name={icon} />;
    }
    return icon;
  }, []);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  /**
   * Context value — memoised so child components only re-render when
   * onHoverOpen or contentSize actually change, not when unrelated
   * DropdownMenu state (isMenuOpen, isButtonHovered) updates.
   */
  const ctxValue = useMemo(
    () => ({
      onHoverOpen,
      submenuOnHover: submenuOnHover ?? onHoverOpen,
      contentSize,
      closeMenu,
      renderIcon,
      getNestedEntries,
    }),
    [
      onHoverOpen,
      submenuOnHover,
      contentSize,
      closeMenu,
      renderIcon,
      getNestedEntries,
    ],
  );

  // Clean up close timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Recompute fixed position on scroll/resize while open
  useEffect(() => {
    if (!isMenuOpen) return;
    const update = () => computeMenuPosition();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isMenuOpen, computeMenuPosition]);

  /**
   * Handles clicks outside the dropdown to close it
   */
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const inMenu = menuRef.current?.contains(event.target as Node);
      const inButton = buttonRef.current?.contains(event.target as Node);
      if (!inMenu && !inButton) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  /**
   * Handles mouse enter event for hover-based opening
   */
  const handleMouseEnter = () => {
    if (disabled) return;

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (onHoverOpen && !isMenuOpen) {
      computeMenuPosition();
      setIsMenuOpen(true);
    }
  };

  /**
   * Handles mouse leave event with delayed closing
   */
  const handleMouseLeave = () => {
    if (!onHoverOpen) return;

    closeTimeoutRef.current = setTimeout(() => {
      setIsMenuOpen(false);
      closeTimeoutRef.current = null;
    }, CLOSE_TIMEOUT_DELAY);
  };

  /**
   * Handles keyboard navigation for accessibility
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsMenuOpen(!isMenuOpen);
    } else if (event.key === "Escape") {
      setIsMenuOpen(false);
    }
  };

  /**
   * Determines which icon to show on the trigger button
   */
  const getCurrentButtonIcon = () => {
    if (isButtonHovered && hoverButtonIcon) {
      return hoverButtonIcon;
    }
    return buttonIcon;
  };

  const triggerButton = (
    <button
      ref={buttonRef}
      // A <button> with no type is type="submit". Inside a <form> — the Indy
      // composer, any dropdown in a form — opening this menu submitted the
      // form instead.
      type="button"
      onClick={() => {
        if (!disabled) {
          if (!isMenuOpen) computeMenuPosition();
          setIsMenuOpen(!isMenuOpen);
        }
      }}
      onKeyDown={handleKeyDown}
      className={`indigo-dropdown-actions-button ${buttonClassName || ""}`}
      onMouseEnter={() => setIsButtonHovered(true)}
      onMouseLeave={() => setIsButtonHovered(false)}
      disabled={disabled}
      data-testid={dataTestId}
      aria-haspopup="menu"
      aria-expanded={isMenuOpen}
      aria-label={triggerAriaLabel}
    >
      {trigger ??
        (() => {
          const currentIcon = getCurrentButtonIcon();
          return currentIcon ? renderIcon(currentIcon) : null;
        })()}
    </button>
  );

  const menuSurface = (
    <div
      ref={menuRef}
      className={`indigo-dropdown-actions-container drop-${dropDirection}${
        tone === "dark" ? " indigo-dropdown-actions-container--dark" : ""
      } ${isMenuOpen ? "open" : ""}`}
      style={{ minWidth: menuWidth || "180px" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={dataTestId ? `${dataTestId}-dropdown-menu` : undefined}
      role="menu"
    >
      <DropdownMenuList entries={items} />
    </div>
  );

  return (
    <DropdownMenuContext.Provider value={ctxValue}>
      <div
        className="indigo-dropdown-actions"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {triggerTooltip ? (
          <Tooltip
            content={triggerTooltip}
            placement={triggerTooltipPlacement}
            trigger="mouseenter"
            disabled={isMenuOpen}
          >
            {triggerButton}
          </Tooltip>
        ) : (
          triggerButton
        )}

        {/* Portalled to <body>. The surface is `position: fixed` and placed in
            viewport coordinates, but any ancestor carrying a `transform`
            becomes the containing block for fixed descendants — and the Indy
            floating panel keeps one permanently (its entrance keyframes use
            `animation-fill-mode: both`) while also clipping with
            `overflow: hidden`. Rendered in place, the menu was positioned
            against the panel and then clipped away, so the trigger looked
            dead. React events still flow through the portal, and the
            click-outside check is DOM `contains`, which the portal does not
            affect. */}
        {typeof document === "undefined"
          ? menuSurface
          : createPortal(menuSurface, document.body)}
      </div>
    </DropdownMenuContext.Provider>
  );
};

DropdownMenu.displayName = "DropdownMenu";
